// Cookie consent for the guest site (OH-72, docs/27 §G1).
//
// ePrivacy Art 5(3) — and EDPB Guidelines 2/2023, which extend it past cookies
// to localStorage — govern READING FROM AND WRITING TO the visitor's device,
// not the counting itself. That is why this module has three states rather
// than two: before a choice we may still count a visit, but we may not leave
// anything behind on the device to recognise it by. Each state maps to a
// different PostHog configuration; lib/posthog.ts is where that mapping lives.
//
//   pending — no choice yet. Analytics runs, but stores NOTHING on the device.
//   granted — full analytics, persistent identity, cross-visit funnel.
//   denied  — silence. PostHog is not initialised at all on the next load.
//
// The decision itself is kept in a first-party cookie. Storing a consent
// choice is "strictly necessary" processing and needs no consent of its own —
// it is the one thing a visitor who refuses everything still gets written,
// because the alternative is asking again on every single page.

export type ConsentDecision = 'pending' | 'granted' | 'denied';

// Bumping this re-asks everyone, and that is the mechanism for widening the
// banner later. A marketing category (a Meta pixel, once there is an ad
// account to point it at) cannot ride on a consent given when the banner
// offered analytics only: a narrower "yes" does not answer a wider question.
export const CONSENT_VERSION = 1;

const COOKIE_NAME = 'ollio_consent';

// Six months. Long enough not to nag a returning guest, short enough that the
// choice still refers to a policy they could plausibly recognise. CNIL treats
// ~6 months as the point where consent should be sought again.
const MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

interface StoredConsent {
  v: number;
  analytics: boolean;
  ts: string;
}

// The live decision. Read ONCE at module load, synchronously, because
// initPostHog() runs before React mounts and needs the answer before it picks
// a persistence mode — see the note in lib/posthog.ts about what happens to a
// returning visitor's first page_view otherwise.
let current: ConsentDecision = readStoredDecision();

type Listener = (decision: ConsentDecision) => void;
const listeners = new Set<Listener>();
const settingsRequests = new Set<() => void>();

function readStoredDecision(): ConsentDecision {
  if (typeof document === 'undefined') return 'pending';
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return 'pending';
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<StoredConsent>;
    // A cookie from an older banner describes a choice about a different set
    // of purposes. Treat it as no choice at all rather than as a "no" — the
    // visitor gets asked again, which is the whole point of versioning it.
    if (parsed.v !== CONSENT_VERSION) return 'pending';
    if (typeof parsed.analytics !== 'boolean') return 'pending';
    return parsed.analytics ? 'granted' : 'denied';
  } catch {
    // A malformed value is not a decision. Ask again.
    return 'pending';
  }
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  // Guarded because this runs at MODULE SCOPE — main.tsx calls initPostHog()
  // before it renders anything, and booking.ts imports this module too. A
  // throw here is a white page on the checkout route, not a missing cookie.
  // (No trigger is known today; the localStorage sweep in posthog.ts is
  // guarded for the same class of reason, and the asymmetry was the bug.)
  let jar = '';
  try {
    jar = document.cookie;
  } catch {
    return null;
  }
  for (const part of jar.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) return trimmed.slice(prefix.length);
  }
  return null;
}

// consentDecision is the live answer, not a snapshot taken at import time.
// PostHog's before_send hook calls this on every event, so a Reject clicked
// mid-session takes effect on the very next one.
export function consentDecision(): ConsentDecision {
  return current;
}

export function analyticsAllowed(): boolean {
  return current === 'granted';
}

// recordConsent persists the choice and tells everyone who cares. It does NOT
// touch PostHog — that is applyConsent's job in lib/posthog.ts, kept separate
// so this module stays testable without a vendor in the way.
//
// Returns whether the browser actually kept it. `document.cookie =` is a
// silent no-op when cookies are blocked for the origin — no throw, no return
// value — and the in-memory decision below would still be correct for this
// page, so the failure only surfaces on the NEXT one, as a banner that comes
// back for ever. For a refusal that is worse than annoying: "pending" means
// cookieless counting resumes, so the guest's no is honoured for exactly one
// page view and then quietly reversed on every navigation.
export function recordConsent(analytics: boolean): boolean {
  const value: StoredConsent = {
    v: CONSENT_VERSION,
    analytics,
    ts: new Date().toISOString(),
  };
  const encoded = encodeURIComponent(JSON.stringify(value));
  writeCookie(COOKIE_NAME, encoded);
  current = analytics ? 'granted' : 'denied';
  for (const listener of listeners) listener(current);
  return readCookie(COOKIE_NAME) === encoded;
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  // `secure` only on https, so the cookie still works on a local http dev
  // server. SameSite=Lax is right for a value that is never needed on a
  // cross-site request and should not ride along with one.
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${name}=${value}; path=/; max-age=${MAX_AGE_SECONDS}; samesite=lax${secure}`;
}

// refreshConsent re-reads the cookie and reports whether the answer changed.
//
// A decision made in ONE tab is invisible to every other tab that is already
// open: `current` is per-document, and the choice lives in a cookie, so not
// even the `storage` event fires. Without this, a guest who withdraws consent
// in one tab leaves another tab still holding a fully persistent PostHog that
// writes the identifier straight back — withdrawal confirmed on screen and
// undone in the background. lib/posthog.ts calls this whenever a tab is
// brought back to the front.
export function refreshConsent(): ConsentDecision | null {
  const stored = readStoredDecision();
  if (stored === current) return null;
  current = stored;
  for (const listener of listeners) listener(current);
  return current;
}

export function subscribeConsent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// The footer's "Cookie settings" link. A visitor must be able to change their
// mind as easily as they made it up (GDPR Art. 7(3)), which in practice means
// a permanent, reachable control — not a banner they can never see again.
export function openConsentSettings(): void {
  for (const request of settingsRequests) request();
}

export function subscribeConsentSettings(request: () => void): () => void {
  settingsRequests.add(request);
  return () => settingsRequests.delete(request);
}
